import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { INTERACTIVE_ANALYTICS } from '../../../../content/staticAssets';

const BASE = INTERACTIVE_ANALYTICS.governanceLayersBase;

const LAYERS = [
  {
    key: 'reporting',
    name: 'Reporting',
    suffix: 'Layer',
    src: `${BASE}/reporting-layer.png`,
    glow: 'rgba(25, 227, 242, 0.22)',
    accent: '#19E3F2',
  },
  {
    key: 'semantic',
    name: 'Semantic',
    suffix: 'Layer',
    src: `${BASE}/semantic-layer.png`,
    glow: 'rgba(0, 18, 63, 0.18)',
    accent: '#00123F',
  },
  {
    key: 'data',
    name: 'Data',
    suffix: 'Layer',
    src: `${BASE}/data-layer.png`,
    glow: 'rgba(0, 180, 254, 0.18)',
    accent: '#00B4FE',
  },
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold, rootMargin: '0px 0px -6% 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

function GovernanceShield() {
  return (
    <svg viewBox="0 0 48 48" className="gov-layer-shield__icon" aria-hidden="true">
      <defs>
        <linearGradient id="gov-layer-shield-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#19E3F2" />
          <stop offset="55%" stopColor="#00B4FE" />
          <stop offset="100%" stopColor="#0D9488" />
        </linearGradient>
        <filter id="gov-layer-shield-shadow" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#00B4FE" floodOpacity="0.35" />
        </filter>
      </defs>
      <circle cx="24" cy="24" r="22" fill="#00B4FE" fillOpacity="0.12" />
      <path
        d="M24 6 L38 12 V24 C38 32 32 38 24 42 C16 38 10 32 10 24 V12 Z"
        fill="url(#gov-layer-shield-grad)"
        filter="url(#gov-layer-shield-shadow)"
      />
      <path
        d="M17 24 L22 29 L31 19"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LayerTitle({ layer, side }) {
  return (
    <div className={`gov-layer-row__title gov-layer-row__title--${side}`} style={{ '--layer-accent': layer.accent }}>
      <span className="gov-layer-row__title-connector" aria-hidden="true" />
      <p className="gov-layer-row__title-text">
        <span className="gov-layer-row__title-name">{layer.name}</span>
        <span className="gov-layer-row__title-suffix">{layer.suffix}</span>
      </p>
    </div>
  );
}

/**
 * Centered governance stack: timeline connectors live only in gaps between icons.
 */
export default function GovernanceLayerStack({
  alt = 'Reporting, semantic, and data layers with governance shield traveling each tier',
}) {
  const reducedMotion = usePrefersReducedMotion();
  const { ref, inView } = useInView();
  const innerRef = useRef(null);
  const anchorRefs = useRef([]);
  const [anchors, setAnchors] = useState(null);

  const measureAnchors = React.useCallback(() => {
    const container = innerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const next = LAYERS.map((_, index) => {
      const el = anchorRefs.current[index];
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      return rect.top - containerRect.top + rect.height / 2;
    });

    if (next.every((y) => y > 0)) {
      setAnchors(next);
    }
  }, []);

  useLayoutEffect(() => {
    measureAnchors();
    window.addEventListener('resize', measureAnchors);
    return () => window.removeEventListener('resize', measureAnchors);
  }, [measureAnchors]);

  useEffect(() => {
    if (!innerRef.current) return undefined;
    const observer = new ResizeObserver(measureAnchors);
    observer.observe(innerRef.current);
    return () => observer.disconnect();
  }, [measureAnchors]);

  const shieldStyle =
    anchors && anchors.length === 3
      ? {
          '--gov-y-reporting': `${anchors[0]}px`,
          '--gov-y-semantic': `${anchors[1]}px`,
          '--gov-y-data': `${anchors[2]}px`,
        }
      : undefined;

  const shieldClass = [
    'gov-layer-shield',
    !reducedMotion && inView && anchors ? 'gov-layer-shield--playing' : '',
    reducedMotion ? 'gov-layer-shield--reduced' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={ref} className="gov-layer-stack w-full">
      <div className="sr-only" role="img" aria-label={alt} />

      <div ref={innerRef} className="gov-layer-stack__inner relative mx-auto w-full max-w-[360px] sm:max-w-[400px]">
        <div className="gov-layer-stack__rows flex flex-col items-center">
          {LAYERS.map((layer, index) => {
            const side = index === 1 ? 'right' : 'left';

            return (
              <React.Fragment key={layer.key}>
                <div className="gov-layer-row relative w-full">
                  <LayerTitle layer={layer} side={side} />

                  <div
                    ref={(el) => {
                      anchorRefs.current[index] = el;
                    }}
                    className="gov-layer-row__stage relative z-10 mx-auto flex h-[92px] w-[92px] items-center justify-center sm:h-[112px] sm:w-[112px]"
                  >
                    <div
                      className="gov-layer-row__glow pointer-events-none absolute inset-0 rounded-full blur-2xl"
                      style={{ background: `radial-gradient(circle, ${layer.glow} 0%, transparent 68%)` }}
                    />
                    <div className="gov-layer-row__icon relative flex h-full w-full items-center justify-center">
                      <img
                        src={layer.src}
                        alt=""
                        className="max-h-full max-w-full object-contain drop-shadow-md"
                        draggable={false}
                        loading="lazy"
                        onLoad={measureAnchors}
                      />
                    </div>
                  </div>
                </div>

                {index < LAYERS.length - 1 ? (
                  <div className="gov-layer-stack__bridge" aria-hidden="true">
                    <div className="gov-layer-stack__segment" />
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </div>

        <div className={shieldClass} style={shieldStyle} aria-hidden="true">
          <div className="gov-layer-shield__halo" />
          <GovernanceShield />
        </div>
      </div>
    </div>
  );
}
